import { type CatalogProduct } from "@/domain/catalog";
import { isPurchasable } from "@/domain/catalog/online-status";
import { type AddToCartButtonState } from "./AddToCartButtonView";

export interface AddToCartStateInput {
	loading?: boolean;
	error?: string | null;
	submitted?: boolean;
}

/**
 * Add-to-Cart button state: whether the product can be bought at all, then the
 * request's own progress.
 *
 * Who may buy it is deliberately not asked here. A product with no storefront
 * audience has no page, and a consultant-only one is redirected away from in
 * middleware, so by the time this renders the viewer is already allowed to buy
 * it. Asking again would mean guessing at who is shopping before the browser
 * has told us, which is what made the button flip after hydration.
 *
 * The lifecycle question is a single call: search-sync has already reconciled
 * PIM, the CMS override and inventory into one status, so there is nothing left
 * here to re-derive.
 */
export function deriveAddToCartState(
	product: Pick<CatalogProduct, "onlineStatus">,
	input: AddToCartStateInput = {}
): AddToCartButtonState {
	const { loading = false, error = null, submitted = false } = input;

	if (!isPurchasable(product.onlineStatus)) {
		return unpurchasableReason(product.onlineStatus);
	}
	if (loading) return { kind: "loading" };
	if (submitted) return { kind: "success" };
	if (error) return { kind: "error", message: error };
	return { kind: "idle" };
}

function unpurchasableReason(
	status: CatalogProduct["onlineStatus"]
): AddToCartButtonState {
	switch (status) {
		case "out-of-stock":
			return { kind: "out-of-stock" };
		case "temporarily-unavailable":
			return { kind: "temporarily-unavailable" };
		case "pre-launch":
			return { kind: "pre-launch" };
		default:
			return { kind: "discontinued" };
	}
}

export function isCartDisabledByState(
	kind: AddToCartButtonState["kind"]
): boolean {
	return (
		kind === "out-of-stock" ||
		kind === "discontinued" ||
		kind === "temporarily-unavailable" ||
		kind === "pre-launch" ||
		kind === "host-exclusive" ||
		kind === "consultant-only-gated"
	);
}
